/**
 * Trip duration and price buckets — single source of truth for the
 * `/trips` filter sidebar, MegaMenu "By Duration" / "By Budget" categories,
 * and the trip-planning quiz.
 *
 * When the admin wants different bucket boundaries or labels, edit here
 * and they propagate everywhere automatically.
 */

export interface TripBucket {
  /** URL-safe identifier used for `?duration=`/`?budget=` query and React keys. */
  key: string;
  /** User-facing label shown in menus, filters, and the quiz. */
  label: string;
  /** Lower bound, inclusive. */
  min: number;
  /** Upper bound, inclusive. `null` means open-ended (infinity). */
  max: number | null;
  /** Emoji shown next to the label in the quiz UI. Optional. */
  icon?: string;
}

/** Trip duration in days. Ranges are contiguous — 2-day trips fall into "short"
 * so the full `[0, ∞)` is covered with no holes. `as const` keeps `key` literals
 * narrow so consumers (zod enums, lookup-by-key) get compile-time safety. */
export const DURATION_BUCKETS = [
  { key: "day", label: "Day trip (half / full day)", min: 0, max: 1, icon: "☀️" },
  { key: "short", label: "Short Breaks (2-6 days)", min: 2, max: 6, icon: "📅" },
  { key: "long", label: "Long trip (7+ days)", min: 7, max: null, icon: "🗓️" },
] as const satisfies readonly TripBucket[];

export type DurationBucketKey = (typeof DURATION_BUCKETS)[number]["key"];

/** Trip price per person in USD. */
export const PRICE_BUCKETS = [
  { key: "lt70", label: "Under $70", min: 0, max: 69, icon: "💰" },
  { key: "70_300", label: "$70 - $300", min: 70, max: 300, icon: "💰💰" },
  { key: "gt300", label: "$300+", min: 301, max: null, icon: "💎" },
] as const satisfies readonly TripBucket[];

export type PriceBucketKey = (typeof PRICE_BUCKETS)[number]["key"];

/** True when `value` falls within `[bucket.min, bucket.max]` (max null = ∞). */
export function inBucket(bucket: TripBucket, value: number): boolean {
  if (value < bucket.min) return false;
  if (bucket.max !== null && value > bucket.max) return false;
  return true;
}

/** Map an array of buckets by key for O(1) lookup. The return type is partial
 * so consumers handle the "unknown key" case explicitly — a raw query-string
 * value like `?budget=typo` would otherwise type-check as a `TripBucket` and
 * NPE on `.min` at runtime. */
export function bucketByKey<B extends TripBucket>(
  buckets: readonly B[],
): Partial<Record<string, B>> {
  return Object.fromEntries(buckets.map((b) => [b.key, b]));
}

/** Find the bucket whose label matches — used to resolve menu links. */
export function bucketByLabel<B extends TripBucket>(
  buckets: readonly B[],
  label: string,
): B | undefined {
  return buckets.find((b) => b.label === label);
}

export const DURATION_BUCKET_BY_KEY = bucketByKey(DURATION_BUCKETS);
export const PRICE_BUCKET_BY_KEY = bucketByKey(PRICE_BUCKETS);

/** Tuple of bucket keys, with literal narrowing, for `z.enum`. */
export const DURATION_BUCKET_KEYS = DURATION_BUCKETS.map((b) => b.key) as unknown as readonly [
  DurationBucketKey,
  ...DurationBucketKey[],
];
export const PRICE_BUCKET_KEYS = PRICE_BUCKETS.map((b) => b.key) as unknown as readonly [
  PriceBucketKey,
  ...PriceBucketKey[],
];
